/**
 * =============================================================================
 * Hook: useReferralCreditsRealtime
 * Versão: 1.0.0 (2026-05-04)
 *
 * Descrição:
 *   Subscreve Postgres Changes em `tenant_referral_credits` e
 *   `tenant_referral_credit_ledger` filtrado por tenant_id e invalida as queries
 *   `['referral-access']` e `['referral-credit-history', ...]` para que o saldo
 *   exibido no header (ReferralUsageInline) e o modal de Histórico atualizem
 *   automaticamente após consumo/estorno/grant — sem necessidade de F5.
 * =============================================================================
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('ReferralCreditsRealtime');

export function useReferralCreditsRealtime(tenantId: string | null | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!tenantId) return;

    logger.debug('Iniciando Realtime de créditos', { tenantId });

    const invalidate = (source: string, eventType: string) => {
      logger.debug('Realtime evento — invalidando caches', { source, eventType });
      queryClient.invalidateQueries({ queryKey: ['referral-access'] });
      queryClient.invalidateQueries({ queryKey: ['referral-credit-history'] });
    };

    const channel = supabase
      .channel(`referral-credits-rt-${tenantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tenant_referral_credits',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => invalidate('tenant_referral_credits', payload.eventType),
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tenant_referral_credit_ledger',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => invalidate('tenant_referral_credit_ledger', payload.eventType),
      )
      .subscribe((status) => {
        logger.debug('Realtime status', { status });
      });

    return () => {
      logger.debug('Removendo Realtime de créditos', { tenantId });
      supabase.removeChannel(channel);
    };
  }, [tenantId, queryClient]);
}
